import moment from "moment";
import { data as constants } from "../../constants";
import {
  db,
  doc,
  addDoc,
  updateDoc,
  setDoc,
  getDoc,
  getDocs,
  Timestamp,
  collection,
  collectionGroup,
  where,
  limit,
  startAfter,
  orderBy,
  query,
  increment,
  arrayUnion,
  refStorage,
  firebaseStorage,
  deleteObject,
  getDownloadURL, 
  uploadBytes,
  writeBatch,
} from "../../firebase";
import {
  ApplicantType,
  FirebaseApplicant,
  TotalApplicants,
  ApplicantDocument,
} from "../entities/applicant";
import { Query as QueryEntity } from "../entities/query";
import ApplicantRepository from "../usecases/ports/applicantRepository";
import { generateNameKeywords, camelize } from "../../utils/helpers/strings";
import axios from "../../axios";
import { isFileNamesSame } from "../../utils/helpers/file";
import { getFileExtension } from "../../utils/strings";

export const APPLICANT_COLLECTION = "applicant";
export default class ApplicantFirebaseImpl implements ApplicantRepository {
  addApplicant = async (applicant: ApplicantType): Promise<any> => {
    try {
      if (!navigator.onLine) {
        throw new Error(constants.commonNetworkError);
      }
      const timestamp = Timestamp.now();
      const validUntil = Timestamp.fromDate(moment().add(1, "years").toDate());
      const { applicantName } = applicant;
      const fullName = `${applicantName.firstName} ${applicantName.middleName} ${applicantName.lastName}`;
      const nameKeywords = generateNameKeywords(applicantName);

      const applicantData = {
        ...applicant,
        createdAt: timestamp,
        updatedAt: timestamp,
        validUntil,
        isDeleted: false,
        applicationStatus: {
          newApplicant: true,
          shortlisted: false,
          technical: false,
          initialInterview: false,
          psychological: false,
          finalInterview: false,
          jobOffer: false,
          subStatus: "Unread",
          currentStatus: "New Applicant",
          dateMoved: timestamp,
        },
        nameKeywords,
        applicantAltName: fullName.toLocaleLowerCase(),
      };

      const applicantRef = collection(db, "applicant");
      const notifRef = collection(db, "notification");

      let { id } = await addDoc(applicantRef, applicantData);

      const logsRef = collection(db, "applicant", id, "logs");
      const commentsRef = collection(db, "applicant", id, "comments");

      await addDoc(logsRef, {
        logs: [{ status: "Applied", dateTime: timestamp }],
      });

      await addDoc(commentsRef, {
        comments: [],
      });

      const notficationData = {
        type: "applied",
        title: `${fullName} submitted an application as a ${applicantData.positionApplied}`,
        description: `${fullName} submitted an application as a ${applicantData.positionApplied}`,
        notificationLink: `/recruit/new-applicants/${id}`,
        applicantName: fullName,
        read: false,
        createdAt: timestamp,
        updatedAt: timestamp,
        user: { id: "", name: "" },
      };

      await addDoc(notifRef, notficationData);
    } catch (error) {
      throw error;
    }
  };

  fetchApplicantsList = async (
    queryEntity: QueryEntity
  ): Promise<{
    data?: FirebaseApplicant[];
    lastElement?: unknown;
    firstElement?: unknown;
  }> => {
    try {
      const {
        where: whereQuery,
        orderBy: orderByQuery,
        limit: limitQuery,
        startAfter: startAfterQuery,
      } = queryEntity;
      const collectionRef = collectionGroup(db, APPLICANT_COLLECTION);
      const queryConstraints = [];
      const transformedData: any[] = [];
      if (whereQuery && whereQuery?.length > 0) {
        whereQuery.forEach((whereItem) => {
          if (
            typeof whereItem[0] !== "undefined" &&
            typeof whereItem[1] !== "undefined" &&
            typeof whereItem[2] !== "undefined"
          ) {
            queryConstraints.push(
              where(whereItem[0], whereItem[1], whereItem[2])
            );
          }
        });
      }
      if (orderByQuery && orderByQuery?.length > 0) {
        orderByQuery.forEach((orderByParams) => {
          const [key, value] = orderByParams;
          queryConstraints.push(orderBy(key, value));
        });
      }
      if (limitQuery) {
        queryConstraints.push(limit(limitQuery));
      }
      if (startAfterQuery) {
        queryConstraints.push(startAfter(startAfterQuery));
      }
      const q = query(collectionRef, ...queryConstraints);
      const docData = await getDocs(q);

      if (docData.empty && docData.metadata.fromCache) {
        throw new Error(constants.commonNetworkError);
      }

      docData.forEach((data) => {
        const _data = data.data();
        transformedData.push({
          id: data.id,
          ..._data,
        });
      });

      return {
        data: transformedData,
        lastElement: docData?.docs[docData?.docs.length - 1],
        firstElement: docData?.docs[0],
      };
    } catch (error) {
      throw error;
    }
  };

  fetchApplicants = async (
    limitEntity?: number,
    whereEntity?: [
      string,
      "<" | "<=" | "==" | ">=" | ">" | "array-contains",
      string | number | boolean
    ][]
  ): Promise<FirebaseApplicant[]> => {
    try {
      const collectionRef = collection(db, APPLICANT_COLLECTION);
      const queryConstraints = [];
      const dataArray: any[] = [];

      queryConstraints.push(
        ...[
          orderBy("createdAt", "asc"),
          orderBy(
            whereEntity ? "applicantAltName" : "applicationStatus.dateMoved",
            "desc"
          ),
        ]
      );

      if (limitEntity) {
        queryConstraints.push(limit(limitEntity));
      }

      if (whereEntity && whereEntity.length > 0) {
        whereEntity.forEach((element) => {
          queryConstraints.push(where(element[0], element[1], element[2]));
        });
      }

      const q = query(collectionRef, ...queryConstraints);
      const docData = await getDocs(q);

      if (docData.empty && docData.metadata.fromCache) {
        throw new Error(constants.commonNetworkError);
      }

      docData.forEach((data) => {
        const _data = data.data();
        dataArray.push({ ..._data, id: data.id });
      });
      return dataArray;
    } catch (error) {
      throw error;
    }
  };

  fetchTotalApplicants = async (): Promise<TotalApplicants> => {
    try {
      let collectionRef = collection(db, "totalApplicants");
      let totalObject = {};
      const docData = await getDocs(collectionRef);

      if (docData.empty && docData.metadata.fromCache) {
        throw new Error(constants.commonNetworkError);
      }

      docData.forEach((data) => {
        const _data = data.data();
        totalObject = {
          ...totalObject,
          [data.id]: {
            ..._data,
          },
        };
      });

      return totalObject as TotalApplicants;
    } catch (error) {
      throw error;
    }
  };

  fetchApplicant = async (id: string): Promise<any> => {
    try {
      let applicantInfo: any = {};
      const docRef = doc(db, APPLICANT_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.metadata.fromCache) {
        window.location.reload();
        throw new Error(constants.commonNetworkError);
      }

      const data = docSnap.data();
      const applicantName = data?.applicantName
        ? `${data?.applicantName.firstName} ${data?.applicantName.middleName} ${data?.applicantName.lastName}`
        : data?.fullName;

      if (docSnap.exists()) {
        applicantInfo = {
          applicantName: {
            firstName: data?.applicantName.firstName,
            middleName: data?.applicantName.middleName,
            lastName: data?.applicantName.lastName,
          },
          fullName: applicantName,
          positionApplied: data?.positionApplied,
          expectedSalary: data?.expectedSalary,
          birthDate: data?.birthDate.toDate(),
          civilStatus: data?.civilStatus,
          degree: data?.degree,
          portfolio: data?.portfolio,
          dateCompleted: data?.dateCompleted.toDate(),
          school: data?.school,
          course: data?.course,
          referredBy: data?.referredBy,
          skills: data?.skills,
          linkedinUrl: data?.linkedinUrl,
          email: data?.email,
          contactNumber: data?.contactNumber,
          contactTimeTo: data?.contactTimeTo,
          contactTimeFrom: data?.contactTimeFrom,
          address: data?.address,
          characterReference: data?.characterReference,
          willingToRelocate: data?.willingToRelocate,
          yearsOfExperience: data?.yearsOfExperience,
          recentWork: data?.recentWork,
          reasonOfLeaving: data?.reasonOfLeaving,
          toolsUsed: data?.toolsUsed,
          agileExperience: data?.agileExperience,
          workOffice: data?.workOffice,
          platformApplied: data?.platformApplied,
          resume: data?.resume,
          applicationStatus: data?.applicationStatus,
          scheduleDate: data?.scheduleDate,
          interviewers: data?.interviewers,
          documents: data?.documents || [],
        };
      }

      let logs: Array<any> = [];

      const logsRef = collection(db, APPLICANT_COLLECTION, id, "logs");
      const logsSnap = await getDocs(logsRef);

      if (logsSnap.empty && logsSnap.metadata.fromCache) {
        throw new Error(constants.commonNetworkError);
      }

      logsSnap.forEach((doc) => {
        let data = doc.data();

        const formattedData = data.logs
          .map((logData: any) => {
            return {
              dateTime: logData.dateTime.toDate(),
              editor: logData.editor,
              status: logData.status,
            };
          })
          .sort((a: any, b: any) => b.dateTime - a.dateTime);

        logs.push({ logs: formattedData, id: doc.id });
      });

      return { ...applicantInfo, logs: logs[0] };
    } catch (error) {
      error === constants.commonNetworkError && window.location.reload();
      throw error;
    }
  };

  checkSchedule = async (
    id: string,
    dateAndTime: string,
    data: Partial<FirebaseApplicant>
  ) => {
    try {
      if (!navigator.onLine) {
        throw new Error(constants.commonNetworkError);
      }
      const { interviewers } = data;
      await axios.put(`/applicant/check-schedule/${id}`, {
        interviewers,
        dateAndTime,
      });
    } catch (error) {
      throw error;
    }
  };

  updateApplicant = async (id: string, data: Partial<FirebaseApplicant>) => {
    try {
      if (!navigator.onLine) {
        throw new Error(constants.commonNetworkError);
      }
      const { interviewers, isUpdateInterviewers, ...otherData } = data;
      if (interviewers && Object.getOwnPropertyNames(otherData).length !== 0) {
        await axios.put(`/applicant/update-interviewers/${id}`, {
          interviewers,
        });
      }

      const applicantRef = doc(db, APPLICANT_COLLECTION, id);

      await updateDoc(applicantRef, {
        ...(otherData && { ...otherData }),
        ...(interviewers &&
          Object.getOwnPropertyNames(otherData).length === 0 && {
            interviewers,
          }),
      });
    } catch (error) {
      throw error;
    }
  };

  updateApplicantDocuments = async (id: string, file: Blob | File, fileName: string):Promise<ApplicantDocument> => {
    try {
      const batch = writeBatch(db);
      const applicantRef = doc(db, APPLICANT_COLLECTION, id);
      const docSnap = await getDoc(applicantRef);

      if (docSnap.metadata.fromCache) {
        throw new Error(constants.commonNetworkError);
      }

      const applicantDocuments = docSnap.data()?.documents || [];
      const isFileNameAlreadyExists = applicantDocuments.some(
        (currentDocument: ApplicantDocument) => isFileNamesSame(currentDocument.name, fileName)
      );

      if(isFileNameAlreadyExists)
        throw Error(constants.applicantErrorMessages.fileNameAlreadyExists);

      const fileExtension = getFileExtension(fileName);
      const storageRef = refStorage(firebaseStorage, `${Date.now()}.${fileExtension}`);

      await uploadBytes(storageRef, file);

      const storageDocumentURL = await getDownloadURL(storageRef);
      const document = { name: fileName, src: storageDocumentURL, dateUploaded: Timestamp.now()};
      batch.update(applicantRef, { documents: arrayUnion(document)});

      await batch.commit();

      return document as ApplicantDocument;
    } catch (error) {
      throw error;
    }
  };

  deleteApplicantDocuments = async (id: string, toBeDeleteddocument: ApplicantDocument):Promise<void> => {
    try {
      const batch = writeBatch(db);
      const applicantRef = doc(db, APPLICANT_COLLECTION, id);
      const docSnap = await getDoc(applicantRef);

      if (docSnap.metadata.fromCache) {
        throw new Error(constants.commonNetworkError);
      }

      const applicantDocuments = docSnap.data()?.documents || [];
      const updatedApplicantDocuments = applicantDocuments.filter((applicantDocument: ApplicantDocument) => 
        applicantDocument.src !== toBeDeleteddocument.src
      );
      batch.update(applicantRef, { documents: updatedApplicantDocuments});

      const documentStorageRef = refStorage(firebaseStorage, toBeDeleteddocument.src);  
      const deleteDocumentPromise = deleteObject(documentStorageRef);

      await Promise.all([deleteDocumentPromise, batch.commit()]);
    } catch (error) {
      throw error;
    }
  };

  updateTotalNewApplicants = async (
    currentPhase: string,
    prevPhase: string,
    currentStatus: string
  ): Promise<any> => {
    try {
      if (!navigator.onLine) {
        throw new Error(constants.commonNetworkError);
      }
      const incrementTotal = increment(1);
      const decrementTotal = increment(-1);

      const updatePhaseTotal = async () => {
        if (prevPhase === "New Applicant") {
          const newApplicantTotalRef = doc(
            db,
            "totalApplicants",
            "newApplicant"
          );
          await setDoc(
            newApplicantTotalRef,
            { read: decrementTotal },
            { merge: true }
          );
        } else {
          const prevPhaseRef = doc(db, "totalApplicants", camelize(prevPhase));
          await setDoc(
            prevPhaseRef,
            { current: decrementTotal },
            { merge: true }
          );
        }
        const currentPhaseRef = doc(
          db,
          "totalApplicants",
          camelize(currentPhase)
        );
        await setDoc(
          currentPhaseRef,
          { current: incrementTotal },
          { merge: true }
        );
      };

      switch (currentPhase) {
        case "New Applicant": {
          if (prevPhase === "New Applicant" && currentStatus === "Read") {
            const newApplicantTotalRef = doc(
              db,
              "totalApplicants",
              "newApplicant"
            );
            await setDoc(
              newApplicantTotalRef,
              {
                read: incrementTotal,
                unread: decrementTotal,
              },
              { merge: true }
            );
          }
          if (prevPhase === "New Applicant" && currentStatus === "Unread") {
            const newApplicantTotalRef = doc(
              db,
              "totalApplicants",
              "newApplicant"
            );
            await setDoc(
              newApplicantTotalRef,
              {
                unread: incrementTotal,
                read: decrementTotal,
              },
              { merge: true }
            );
          }
          break;
        }
        case "Failed": {
          if (prevPhase === "New Applicant") {
            const newApplicantTotalRef = doc(
              db,
              "totalApplicants",
              "newApplicant"
            );
            await setDoc(
              newApplicantTotalRef,
              {
                read: decrementTotal,
                failed: incrementTotal,
              },
              { merge: true }
            );
          } else {
            const prevPhaseRef = doc(
              db,
              "totalApplicants",
              camelize(prevPhase)
            );
            await setDoc(
              prevPhaseRef,
              {
                current: decrementTotal,
                failed: incrementTotal,
              },
              { merge: true }
            );
          }
          break;
        }
        default:
          await updatePhaseTotal();
          break;
      }
    } catch (error) {
      throw error;
    }
  };
}
